import { useState } from 'react';
import {
  AlertTriangle,
  Bike,
  Camera,
  CheckCircle2,
  Clock,
  ExternalLink,
  ImageIcon,
  Maximize2,
  Phone,
  Store,
  User,
  Wrench,
} from 'lucide-react';
import type { CrmCycleDamageSummary } from '@/api/bi/crm';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

function resolveMediaUrl(file: string | null | undefined): string | null {
  if (!file) return null;
  if (/^https?:\/\//i.test(file)) return file;
  const base = import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
  return `${base}${file.startsWith('/') ? '' : '/'}${file}`;
}

interface Props {
  damagedCycles?: CrmCycleDamageSummary[];
  className?: string;
}

export function CycleDamageCard({ damagedCycles = [], className }: Props) {
  const totalCount = damagedCycles.length;
  const repairedCount = damagedCycles.filter((c) => Boolean(c.repaired_at)).length;
  const pendingCount = totalCount - repairedCount;

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Bike className="h-4 w-4 text-primary" /> Cycle Damage Info
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {totalCount > 0 && repairedCount > 0 && (
            <Badge tone="success" className="gap-1 text-[11px]">
              <CheckCircle2 className="h-3 w-3" /> {repairedCount} repaired
            </Badge>
          )}
          {totalCount > 0 && pendingCount > 0 && (
            <Badge tone="warning" className="gap-1 text-[11px]">
              <Clock className="h-3 w-3" /> {pendingCount} awaiting repair
            </Badge>
          )}
          <Badge tone={totalCount > 0 ? 'neutral' : 'neutral'}>
            {totalCount} {totalCount === 1 ? 'cycle' : 'cycles'}
          </Badge>
        </div>
      </CardHeader>

      <CardBody className="space-y-3 text-sm">
        <p className="text-[11px] text-muted-foreground">
          Recorded cycle damage incidents, operational status, and repair tracking for this ticket.
        </p>

        {totalCount === 0 ? (
          <EmptyState
            icon={<Bike className="h-6 w-6 text-muted-foreground" />}
            title="No cycle damage reports"
            description="No damaged cycle incidents have been attached to this ticket."
          />
        ) : (
          <div className="max-h-[520px] overflow-y-auto space-y-3 pr-1.5 scrollbar-thin">
            {damagedCycles.map((damage) => (
              <CycleDamageItemCard key={damage.id} damage={damage} />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// Named alias for convenience
export const CycleDamageCards = CycleDamageCard;

function CycleDamageItemCard({ damage }: { damage: CrmCycleDamageSummary }) {
  const imageUrl = resolveMediaUrl(damage.damaged_image);
  const repairedImageUrl = resolveMediaUrl(damage.repaired_image);
  const [imgError, setImgError] = useState(false);
  const [repairedImgError, setRepairedImgError] = useState(false);
  const isRepaired = Boolean(damage.repaired_at);
  const cycleIdentifier = damage.cycle?.identifier || `Cycle #${damage.cycle?.id ?? damage.id}`;

  return (
    <div className="rounded-lg border border-border bg-muted/15 p-4 transition-all hover:border-border-hover hover:bg-muted/25 hover:shadow-xs space-y-3">
      {/* 1. Header Row: Cycle Identifier, Status, and Repair Badge */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Bike className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-sm font-semibold text-foreground truncate">
                {cycleIdentifier}
              </span>
              <Badge
                tone={damage.cycle?.is_operational ? 'success' : 'danger'}
                className="text-[10px]"
              >
                {damage.cycle?.is_operational ? 'Operational' : 'Non-operational'}
              </Badge>
              {damage.cycle?.status && (
                <Badge tone="neutral" className="text-[10px] capitalize">
                  {damage.cycle.status}
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground font-mono">
              Incident #{damage.id}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {isRepaired ? (
            <Badge tone="success" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Repaired
            </Badge>
          ) : (
            <Badge tone="warning" className="gap-1">
              <Clock className="h-3 w-3" /> Awaiting Repair
            </Badge>
          )}
        </div>
      </div>

      {/* 2. Damage Reason & Timestamp Box */}
      <div className="rounded-md border border-warning/20 bg-warning/5 p-3 text-xs">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning mt-0.5" />
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-warning-soft">
                Damage Reason
              </span>
              {damage.damaged_at && (
                <span className="text-[11px] text-muted-foreground">
                  {fmt.datetime(damage.damaged_at)} ({fmt.ago(damage.damaged_at)})
                </span>
              )}
            </div>
            <p className="text-xs font-medium text-foreground whitespace-pre-wrap leading-relaxed">
              {damage.damage_reason || 'No damage reason provided.'}
            </p>
          </div>
        </div>
      </div>

      {/* 3. Damaged Image (with Lightbox Dialog) */}
      {imageUrl && !imgError && (
        <div className="flex items-start gap-3 rounded-md border border-border/70 bg-background/50 p-2.5">
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="group relative h-20 w-24 shrink-0 overflow-hidden rounded-md border border-border bg-black/10 focus-ring cursor-pointer"
                title="Click to zoom image"
              >
                <img
                  src={imageUrl}
                  alt={`Damage photo for ${cycleIdentifier}`}
                  className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                  onError={() => setImgError(true)}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 text-white">
                  <Maximize2 className="h-4 w-4" />
                </div>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogTitle className="text-base font-semibold">
                Damage Evidence — {cycleIdentifier}
              </DialogTitle>
              <div className="mt-3 overflow-hidden rounded-md border border-border bg-black/5 flex items-center justify-center">
                <img
                  src={imageUrl}
                  alt={`Damage photo for ${cycleIdentifier}`}
                  className="max-h-[70vh] w-full object-contain"
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate">
                  Incident #{damage.id} · {damage.damage_reason}
                </span>
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Open original <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </DialogContent>
          </Dialog>
          <div className="min-w-0 flex-1 space-y-1 self-center">
            <div className="text-[11px] font-medium text-foreground inline-flex items-center gap-1.5">
              <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
              Attached Photo Evidence
            </div>
            <p className="text-[11px] text-muted-foreground">
              Click thumbnail to inspect full-size photo.
            </p>
          </div>
        </div>
      )}

      {/* 4. Repair Resolution Section (if repaired or has repair notes or repaired image) */}
      {(damage.repaired_at || damage.repaired_note || damage.repaired_image) && (
        <div className="rounded-md border border-success/20 bg-success/5 p-2.5 text-xs space-y-2.5">
          <div className="flex items-start gap-2">
            <Wrench className="h-3.5 w-3.5 shrink-0 text-success mt-0.5" />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px] font-medium text-success-soft">
                  Repair Resolution
                </span>
                {damage.repaired_at && (
                  <span className="text-[11px] text-muted-foreground">
                    {fmt.datetime(damage.repaired_at)} ({fmt.ago(damage.repaired_at)})
                  </span>
                )}
              </div>
              {damage.repaired_note && (
                <p className="text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed">
                  {damage.repaired_note}
                </p>
              )}
            </div>
          </div>

          {/* Repaired Photo (with Lightbox Dialog) */}
          {repairedImageUrl && !repairedImgError && (
            <div className="flex items-start gap-3 rounded-md border border-success/30 bg-background/60 p-2">
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="group relative h-16 w-20 shrink-0 overflow-hidden rounded-md border border-border bg-black/10 focus-ring cursor-pointer"
                    title="Click to zoom repaired photo"
                  >
                    <img
                      src={repairedImageUrl}
                      alt={`Repaired cycle photo for ${cycleIdentifier}`}
                      className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      onError={() => setRepairedImgError(true)}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 text-white">
                      <Maximize2 className="h-3.5 w-3.5" />
                    </div>
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogTitle className="text-base font-semibold">
                    Repair Evidence — {cycleIdentifier}
                  </DialogTitle>
                  <div className="mt-3 overflow-hidden rounded-md border border-border bg-black/5 flex items-center justify-center">
                    <img
                      src={repairedImageUrl}
                      alt={`Repaired cycle photo for ${cycleIdentifier}`}
                      className="max-h-[70vh] w-full object-contain"
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">
                      Incident #{damage.id}
                      {damage.repaired_note ? ` · ${damage.repaired_note}` : ''}
                    </span>
                    <a
                      href={repairedImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      Open original <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </DialogContent>
              </Dialog>
              <div className="min-w-0 flex-1 space-y-0.5 self-center">
                <div className="text-[11px] font-medium text-foreground inline-flex items-center gap-1.5">
                  <Camera className="h-3.5 w-3.5 text-success" />
                  Repaired Photo Evidence
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Click thumbnail to inspect full-size photo.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 5. Metadata Row: Store & Reporter */}
      <div className="grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2 text-xs text-muted-foreground border-t border-border/50">
        <div className="flex items-center gap-2 min-w-0">
          <Store className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">
            Store:{' '}
            <span className="font-medium text-foreground">{damage.store_name}</span>{' '}
            <span className="text-[11px] text-muted-foreground">
              (#{damage.store_id})
            </span>
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
          <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">
            Reported by:{' '}
            <span className="font-medium text-foreground">
              {damage.reported_by?.name || 'Staff'}
            </span>
          </span>
          {damage.reported_by?.role && (
            <Badge tone="neutral" className="text-[10px] uppercase">
              {damage.reported_by.role}
            </Badge>
          )}
          {damage.reported_by?.phone && (
            <a
              href={`tel:${damage.reported_by.phone}`}
              className="ml-auto inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
              title={`Call ${damage.reported_by.name}`}
            >
              <Phone className="h-3 w-3" />
              {damage.reported_by.phone}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}